import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { dealsApi, type Deal, type RelinkOrphaned } from '../api/deals'
import { usersApi, customStatusesApi, customFieldsApi, type CustomField as CustomFieldDef } from '../api/settings'
import { organizationsApi } from '../api/contacts'
import { InlineField, InlineSelectField } from '../components/InlineField'
import { ActivityTimeline } from '../components/ActivityTimeline'
import { ComposeArea } from '../components/ComposeArea'
import { SLATimer } from '../components/SLATimer'
import { Modal } from '../components/ui/Modal'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { PageSpinner } from '../components/ui/Spinner'
import { formatDate, formatCurrency, fullName } from '../lib/utils'
import { usePageTitle } from '../hooks/usePageTitle'
import { ArrowLeft, Edit, Trash2, AlertTriangle, X } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import { DropdownSelect } from '../components/ui/DropdownSelect'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/auth'

export function DealDetailPage() {
  const { companyId, dealId } = useParams<{ companyId: string; dealId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const user = useAuthStore(s => s.user)
  const isSalesUser = user?.role === 'sales_user'

  const { data: deal, isLoading } = useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => dealsApi.get(companyId!, dealId!).then(r => r.data),
    enabled: !!companyId && !!dealId,
  })
  usePageTitle(deal?.title ?? 'Deal')

  const { data: statuses } = useQuery({
    queryKey: ['custom-statuses', companyId],
    queryFn: () => customStatusesApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })

  const { data: users } = useQuery({
    queryKey: ['users', companyId],
    queryFn: () => usersApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })

  const { data: orgs } = useQuery({
    queryKey: ['organizations', companyId],
    queryFn: () => organizationsApi.list(companyId!, { limit: 500 }).then(r => r.data.data),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })

  const { data: customFieldDefs } = useQuery({
    queryKey: ['custom-fields', companyId, 'deal'],
    queryFn: () => customFieldsApi.list(companyId!, 'deal').then(r =>
      r.data.filter((f: CustomFieldDef) => f.isActive)
        .sort((a: CustomFieldDef, b: CustomFieldDef) => a.sortOrder - b.sortOrder)
    ),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })

  const deleteMutation = useMutation({
    mutationFn: () => dealsApi.remove(companyId!, dealId!),
    onSuccess: () => {
      toast.success('Deal deleted')
      // Invalidate the cached lists so the user lands on /deals with a
      // fresh fetch instead of seeing the just-deleted row in the cache
      // until manual refresh. Reported by user 2026-05-04.
      qc.invalidateQueries({ queryKey: ['deals', companyId] })
      qc.invalidateQueries({ queryKey: ['deals-all', companyId] })
      navigate(`/company/${companyId}/deals`)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete deal')
    },
  })

  if (isLoading || !deal) return <PageSpinner />

  const dealStatuses = statuses?.filter(s => s.applyTo === 'deal') ?? []

  return (
    <div className="flex flex-col h-full">
      {deal.relinkOrphaned && (
        <RelinkOrphanedBanner
          dealId={deal.id}
          companyId={companyId!}
          relinkOrphaned={deal.relinkOrphaned}
        />
      )}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm p-1.5">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="page-title">{deal.title}</h1>
          <SLATimer deadline={deal.slaDeadline} breached={deal.slaBreahed} />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditOpen(true)} className="btn btn-secondary btn-sm">
            <Edit className="w-3.5 h-3.5" /> Edit
          </button>
          <button
            onClick={() => setConfirmDelete(true)}
            className="btn btn-ghost btn-sm text-red-600"
            aria-label="Delete deal"
            title="Delete deal"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-[3] overflow-y-auto px-6 py-4 space-y-4">
          <ComposeArea companyId={companyId!} refType="deals" refId={dealId!} />
          <ActivityTimeline companyId={companyId!} refType="deals" refId={dealId!} />
        </div>

        <div className="flex-[2] overflow-y-auto border-l border-gray-200 bg-white px-5 py-4 space-y-6">
          <div>
            <p className="section-title mb-2">Stage</p>
            <DropdownSelect
              value={deal.status}
              options={dealStatuses.map(s => ({ value: s.label, label: s.label }))}
              onChange={val => {
                dealsApi.updateStage(companyId!, dealId!, val)
                  .then(() => {
                    qc.invalidateQueries({ queryKey: ['deal', dealId] })
                    qc.invalidateQueries({ queryKey: ['deals', companyId] })
                    qc.invalidateQueries({ queryKey: ['dashboard-overview', companyId] })
                  })
              }}
              placeholder="Select stage"
            />
          </div>

          <div>
            <p className="section-title mb-2">Assigned To</p>
            <DropdownSelect
              value={deal.assignedTo ?? ''}
              options={[
                { value: '', label: 'Unassigned' },
                ...(users ?? []).map(u => ({ value: u.id, label: fullName(u.firstName, u.lastName) })),
              ]}
              onChange={val => {
                dealsApi.update(companyId!, dealId!, { assignedTo: val || undefined })
                  .then(() => {
                    qc.invalidateQueries({ queryKey: ['deal', dealId] })
                    qc.invalidateQueries({ queryKey: ['dashboard-overview', companyId] })
                  })
              }}
              placeholder="Unassigned"
            />
          </div>

          <div>
            <p className="section-title mb-2">Details</p>
            <dl className="space-y-2">
              {[
                { label: 'Organization', value: deal.organizationId ? ((orgs ?? []).find(o => o.id === deal.organizationId)?.name ?? '—') : '—' },
                { label: 'Value', value: formatCurrency(deal.value, deal.currency) },
                { label: 'Close Date', value: formatDate(deal.expectedCloseDate) },
                { label: 'Closed At', value: formatDate(deal.closedAt) },
                { label: 'Created', value: formatDate(deal.createdAt) },
                { label: 'Updated', value: formatDate(deal.updatedAt) },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-start">
                  <dt className="w-28 text-xs text-gray-500 font-medium flex-shrink-0 pt-0.5">{label}</dt>
                  <dd className="text-sm text-gray-900 flex-1">{value ?? '—'}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Custom Fields */}
          {customFieldDefs && customFieldDefs.length > 0 && (
            <div>
              <p className="section-title mb-1">Custom Fields</p>
              <dl className="divide-y divide-gray-50">
                {customFieldDefs.map((field: CustomFieldDef) => {
                  const rawVal = (deal.customFields ?? {})[field.fieldKey]
                  const strVal = rawVal != null ? String(rawVal) : ''
                  const isReadOnly = field.isFromSource || isSalesUser

                  if (field.fieldType === 'select' && field.options?.length) {
                    return (
                      <InlineSelectField
                        key={field.fieldKey}
                        label={field.label}
                        value={strVal}
                        options={field.options.map(o => ({ value: o.value, label: o.label }))}
                        onSave={v => dealsApi.update(companyId!, dealId!, { customFields: { ...(deal.customFields ?? {}), [field.fieldKey]: v } })
                          .then(() => {
                            qc.invalidateQueries({ queryKey: ['deal', dealId] })
                            qc.invalidateQueries({ queryKey: ['deals', companyId] })
                          })}
                        readOnly={isReadOnly}
                      />
                    )
                  }

                  const inputType = field.fieldType === 'number' || field.fieldType === 'decimal' ? 'number'
                    : field.fieldType === 'date' ? 'date'
                    : field.fieldType === 'datetime' ? 'datetime-local'
                    : field.fieldType === 'email' ? 'email'
                    : field.fieldType === 'url' ? 'url'
                    : 'text'

                  return (
                    <InlineField
                      key={field.fieldKey}
                      label={field.label}
                      value={strVal}
                      type={inputType as any}
                      onSave={v => dealsApi.update(companyId!, dealId!, { customFields: { ...(deal.customFields ?? {}), [field.fieldKey]: v } })
                        .then(() => {
                          qc.invalidateQueries({ queryKey: ['deal', dealId] })
                          qc.invalidateQueries({ queryKey: ['deals', companyId] })
                        })}
                      readOnly={isReadOnly}
                    />
                  )
                })}
              </dl>
            </div>
          )}
        </div>
      </div>

      {editOpen && deal && (
        <EditDealModal
          deal={deal}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          companyId={companyId!}
          users={users ?? []}
          statuses={dealStatuses}
          onSuccess={() => {
            setEditOpen(false)
            qc.invalidateQueries({ queryKey: ['deal', dealId] })
          }}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete this deal?"
        message={`"${deal.title}" will be removed. This is a soft-delete — the row stays in the database but disappears from the UI.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}

interface EditDealFormData {
  title: string; status: string; value?: number; currency: string
  expectedCloseDate?: string; assignedTo?: string
}

function EditDealModal({ deal, open, onClose, companyId, users, statuses, onSuccess }: {
  deal: Deal; open: boolean; onClose: () => void; companyId: string
  users: { id: string; firstName: string; lastName: string }[]
  statuses: { id: string; label: string }[]
  onSuccess: () => void
}) {
  const { register, handleSubmit, control } = useForm<EditDealFormData>({
    defaultValues: {
      title: deal.title, status: deal.status,
      value: deal.value ?? undefined, currency: deal.currency,
      expectedCloseDate: deal.expectedCloseDate?.slice(0, 10) ?? '',
      assignedTo: deal.assignedTo ?? '',
    },
  })
  const mutation = useMutation({
    mutationFn: (dto: EditDealFormData) => dealsApi.update(companyId, deal.id, dto),
    onSuccess: () => { toast.success('Deal updated'); onSuccess() },
  })
  return (
    <Modal open={open} onClose={onClose} title="Edit Deal" size="lg">
      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
        <div><label className="label">Title</label><input {...register('title', { required: true })} className="input" /></div>
        <div>
          <label className="label">Stage</label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <DropdownSelect
                value={field.value}
                options={statuses.map(s => ({ value: s.label, label: s.label }))}
                onChange={field.onChange}
                placeholder="Select stage"
              />
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">Value</label><input {...register('value', { valueAsNumber: true })} type="number" min="0" step="0.01" className="input" /></div>
          <div><label className="label">Currency</label><input {...register('currency')} className="input" /></div>
        </div>
        <div><label className="label">Expected Close Date</label><input {...register('expectedCloseDate')} type="date" className="input" /></div>
        <div>
          <label className="label">Assigned To</label>
          <Controller
            name="assignedTo"
            control={control}
            render={({ field }) => (
              <DropdownSelect
                value={field.value ?? ''}
                options={[
                  { value: '', label: 'Unassigned' },
                  ...users.map(u => ({ value: u.id, label: fullName(u.firstName, u.lastName) })),
                ]}
                onChange={field.onChange}
                placeholder="Unassigned"
              />
            )}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">Cancel</button>
          <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">Save Changes</button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Surfaced when a restored deal's source lead has since been re-converted
 * to a different deal. Tells the user the link is broken and points at
 * the new deal so they can decide what to do (delete one, edit the lead,
 * etc.). Dismissable per-deal via localStorage so it doesn't keep
 * shouting on every page load.
 */
function RelinkOrphanedBanner({
  dealId, companyId, relinkOrphaned,
}: {
  dealId: string
  companyId: string
  relinkOrphaned: RelinkOrphaned
}) {
  const navigate = useNavigate()
  const dismissKey = `deal-relink-banner-dismissed:${dealId}`
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(dismissKey))
  if (dismissed) return null

  const { sourceLead } = relinkOrphaned
  const leadName = fullName(sourceLead.firstName, sourceLead.lastName)

  return (
    <div className="flex items-start gap-3 px-6 py-2.5 bg-amber-50 border-b border-amber-200 text-sm text-amber-900">
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
      <div className="flex-1">
        This deal was created from <strong>{leadName}</strong>, but that lead has since been
        re-converted to a {sourceLead.currentDealId ? 'different deal' : 'new deal'}.{' '}
        {sourceLead.currentDealId && (
          <button
            onClick={() => navigate(`/company/${companyId}/deals/${sourceLead.currentDealId}`)}
            className="underline hover:text-amber-800"
          >
            Open the current deal →
          </button>
        )}
      </div>
      <button
        onClick={() => { localStorage.setItem(dismissKey, '1'); setDismissed(true) }}
        className="p-0.5 hover:bg-amber-100 rounded text-amber-700"
        aria-label="Dismiss"
        title="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
